import { action, computed, makeObservable, observable } from 'mobx';
import { singleton } from 'tsyringe';

import { CreateEssayCollaboration } from '@/application/collaborations/create-essay.collaboration';
import { CreateEssayDraftCollaboration } from '@/application/collaborations/create-essay-draft.collaboration';
import { CreateEssayMediaCollaboration } from '@/application/collaborations/create-essay-media.collaboration';
import { DeleteEssayDraftCollaboration } from '@/application/collaborations/delete-essay-draft.collaboration';
import { GetAllEssayThemesCollaboration } from '@/application/collaborations/get-all-essay-themes.collaboration';
import { GetAllEssaysCollaboration } from '@/application/collaborations/get-all-essays.collaboration';
import { GetEssayCollaboration } from '@/application/collaborations/get-essay.collaboration';
import { GetEssayHighlightsCollaboration } from '@/application/collaborations/get-essay-highlights.collaboration';
import { GetEssayThemeCollaboration } from '@/application/collaborations/get-essay-theme.collaboration';
import { GetStudentClassByClassifierCollaboration } from '@/application/collaborations/get-student-class-by-classifier.collaboration';
import { UpdateEssayCollaboration } from '@/application/collaborations/update-essay.collaboration';
import {
  type AllEssayDto,
  type EssayDto,
  type EssayHighlightDto,
  type EssayThemeDto,
} from '@/application/interfaces/pegasus.gateway';
import { MixpanelContentEssayCardClick, Tracking } from '@/application/utils/tracking';
import { ClassifiersModel } from '@/presentation/models/classifiers.model';

import { UserModel } from '../models/user.model';
import { essayStatusEnum } from '../screens/essay/components/essay-item/EssayItem';
import type { EssayInput, EssayMediaInput } from '../screens/essay/types';

@singleton()
export class EssayStore {
  @observable currentEssay: EssayDto;
  @observable essays: AllEssayDto;
  @observable currentEssayTheme: EssayThemeDto;
  @observable essayThemes: EssayThemeDto[];
  @observable essayHighlights: EssayHighlightDto[];
  @observable selectedClassifier: any;
  @observable currentStudentClass: { tags: string[]; title: string; guid: string };
  @observable transcribedEssay: string;

  constructor(
    private readonly createEssayCollaboration: CreateEssayCollaboration,
    private readonly createEssayMediaCollaboration: CreateEssayMediaCollaboration,
    private readonly createEssayDraftCollaboration: CreateEssayDraftCollaboration,
    private readonly updateEssayCollaboration: UpdateEssayCollaboration,
    private readonly deleteEssayDraftCollaboration: DeleteEssayDraftCollaboration,
    private readonly getAllEssayThemesCollaboration: GetAllEssayThemesCollaboration,
    private readonly getEssayThemeCollaboration: GetEssayThemeCollaboration,
    private readonly getAllEssaysCollaboration: GetAllEssaysCollaboration,
    private readonly getEssayCollaboration: GetEssayCollaboration,
    private readonly getEssayHighlightsCollaboration: GetEssayHighlightsCollaboration,
    private readonly classifiersModel: ClassifiersModel,
    private readonly userModel: UserModel,
    private readonly getStudentClassByClassifierCollaboration: GetStudentClassByClassifierCollaboration,
  ) {
    makeObservable(this);
  }

  @action
  trackingContentEssayCardEvent = (essay: any) => {
    const props: MixpanelContentEssayCardClick = {
      essayGuid: essay.essayGuid,
      essayStatus: essay.enumEssayStatus,
      essayTitle: essay.essayTitle,
      essayDescription: essay.essayDescription || '',
      essayScore: essay.essayScore || 0,
      user: essay.user,
      student_id: essay.student_id,
      email: essay.email,
      topicGuid: essay.topicGuid,
      topicName: essay.topicName,
      studentClassGuid: essay.studentClassGuid,
      studentClassTags: essay.studentClassTags,
      studentClassTitle: essay.studentClassTitle,
    };
    Tracking.contentEssayCardClick(props);
  };

  @action.bound
  async setCurrentTheme(theme: any) {
    this.currentEssayTheme = theme;
  }

  @action.bound
  async createEssay(essayInput: EssayInput) {
    try {
      if (!essayInput && !essayInput.content) {
        return;
      }

      const classifierGuid = this.classifiersModel.selectedClassifier?.guid;
      if (!classifierGuid) return;

      essayInput.metadata = { classifierGuid };

      const { data } = await this.createEssayCollaboration.run(essayInput);

      if (data) {
        this.currentEssay = data;
        return this.currentEssay;
      }

      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async createEssayMedia(essayInput: EssayMediaInput) {
    try {
      if (!essayInput && !essayInput.file) {
        return;
      }

      const { text } = (await this.createEssayMediaCollaboration.run(essayInput)) as any;

      if (text) {
        this.transcribedEssay = text;
        return text;
      }

      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async createEssayDraft(essayInput: EssayInput) {
    try {
      if (!essayInput && !essayInput.content) {
        return;
      }

      const classifierGuid = this.classifiersModel.selectedClassifier?.guid;
      if (!classifierGuid) return;

      essayInput.metadata = { classifierGuid };

      const { data } = await this.createEssayDraftCollaboration.run(essayInput);

      if (data) {
        this.currentEssay = data;
        return this.currentEssay;
      }

      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async updateEssayDraft(essayInput: EssayInput, id: string) {
    try {
      if (!essayInput && !essayInput.content) {
        return;
      }

      const classifierGuid = this.classifiersModel.selectedClassifier?.guid;
      if (!classifierGuid) return;

      essayInput.metadata = { classifierGuid };

      essayInput.id = id;

      const { data } = await this.updateEssayCollaboration.run(essayInput);

      if (data) {
        this.currentEssay = data;
        return this.currentEssay;
      }

      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async deleteEssayDraft(guid: string) {
    try {
      if (guid) {
        return;
      }

      await this.deleteEssayDraftCollaboration.run(guid);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getAllEssayThemes() {
    try {
      const { data } = await this.getAllEssayThemesCollaboration.run();

      if (data && data.length > 0) {
        this.essayThemes = data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getEssayTheme(guid: string) {
    try {
      const { data } = await this.getEssayThemeCollaboration.run(guid);

      if (data) {
        this.currentEssayTheme = data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getAllEssays() {
    try {
      const { data } = await this.getAllEssaysCollaboration.run();

      this.essays = data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getEssay(guid: string) {
    try {
      const { data } = await this.getEssayCollaboration.run(guid);

      if (data) {
        //related to the trackingEvent clicked content Essay
        this.trackingContentEssayCardEvent({
          essayGuid: data.id,
          enumEssayStatus: this.convertStrToEnum(data.status),
          essayTitle: data.title || '',
          essayDescription: data.content,
          essayScore: data.enemFeedback.overallScore || 0,
          user: this.user.name,
          studentGuid: this.user.guid,
          email: this.user.email,
          topicGuid: data.topic.id || '',
          topicName: data.topic.name || '',
          studentClassGuid: this.currentStudentClass.guid,
          studentClassTags: this.currentStudentClass.tags,
          studentClassTitle: this.currentStudentClass.title,
        });

        this.currentEssay = data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getAllEssayHighlights() {
    try {
      const { data } = await this.getEssayHighlightsCollaboration.run();

      this.essayHighlights = data;
    } catch (error) {
      console.error(error);
    }
  }

  @computed
  get user() {
    return this.userModel.user;
  }

  @action.bound
  async getStudentClassByClassifier() {
    const { data: studentClasses } = await this.getStudentClassByClassifierCollaboration.run(
      this.classifiersModel.selectedClassifier.guid,
    );

    const { guid, title, tags } = studentClasses;
    this.currentStudentClass = { guid, title, tags };
  }

  private convertStrToEnum = (convertingStr: string): essayStatusEnum => {
    switch (convertingStr) {
      case 'DRAFT':
        return essayStatusEnum.DRAFT;
      case 'SENT':
        return essayStatusEnum.SENT;
      case 'DONE':
        return essayStatusEnum.DONE;
    }
  };
}
